import { splitAndTrimCategories } from 'store/userOptions/transformPoiOptions';
import { CodeNameModel, FileList } from 'components/common/types';
import { Environment, RouteFrameCode } from 'components/common/types/Deal.types';
import {
  DirectSalesFilterItem,
  DirectSalesFilter,
  DirectSalesFilterTypes,
  DirectSalesNestedFilter,
  DirectSalesMediaType,
  DirectSalesUnitIncludedExcludedFilter,
  DirectSalesUnitListFilter,
  DirectSalesLogicFilterTypes,
  DirectSalesFilterGroupNames,
  DirectSalesPOIFilter,
  DirectSalesCustomPOIFilter,
} from 'components/common/types/DirectSalesCampaign.types';
import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';
import { POI } from 'components/common/types/Planner.types';
import { SelectedSegment } from 'components/common/types/Segment.types';
import { transformCustomPoiToFilter, transformPoiToFilter } from 'utils/transformPoi';
import { CUSTOM_POI_DATA_PROVIDER } from '../../PlannerSections/DataTargetingContent/PointsOfInterest/consts';

export const mappedFormatFilterName: Record<string, string> = {
  'Business Area': 'BusinessAreaFilter',
  'Size Code': 'FrameSizeFamilyFilter',
  'Frame Size': 'FrameSizeFilter',
  'Product Format': 'ProductFormatFilter',
  'Revenue Stream': 'RevenueStreamFilter',
  'Marketing Name': 'ProductFormatMarketingFilter',
  Network: 'NetworkFilter',
};

export const mapLocationFilter: Record<string, string> = {
  'Route Town': 'RouteTownFilter',
  Postcode: 'PostcodeFilter',
  'TV Area': 'TvAreaFilter',
  Conurbation: 'ConurbationFilter',
  Country: 'CountryFilter',
  'Geographical Area Level 1': 'GeographicalAreaLevelFilter',
  'Geographical Area Level 2': 'GeographicalAreaLevelFilter',
  'Geographical Area Level 3': 'GeographicalAreaLevelFilter',
  County: 'CountyFilter',
  Town: 'TownFilter',
  District: 'DistrictFilter',
};

export const mapTagFilter: Record<string, string> = {
  Tag: 'TagFilter',
};

export const mapNetworkFilter: Record<string, string> = {
  Network: 'networkFilter',
};

export const mapPoiFilter: Record<string, string> = {
  PoiFilter: 'PoiFilter',
  CustomPoiFilter: 'CustomPoiFilter',
};

export const reversedFilterNames = (mappedFilters: Record<string, string>): Record<string, string> => {
  const reversedFilters: Record<string, string> = {};

  Object.entries(mappedFilters).forEach(([key, value]) => {
    reversedFilters[value] = key;
  });

  return reversedFilters;
};

export const transformDealLineFilters = (
  dealLineFilters: CodeNameModel[],
  filterNames: Record<string, string>,
): DirectSalesFilter => {
  const includedFilters: DirectSalesFilterItem[] = [];
  const excludedFilters: DirectSalesFilterItem[] = [];

  dealLineFilters.forEach(({ category, code, name, include }) => {
    if (!category) return;

    const [filterType, ...rest] = Object.values(filterNames);

    const filter = {
      type: rest.length ? filterNames[category] : filterType,
      id: code,
      name,
    };

    if (include) {
      includedFilters.push(filter);
    } else {
      excludedFilters.push(filter);
    }
  });

  const notFilter = {
    type: DirectSalesFilterTypes.NotFilter,
    filter: {
      type: DirectSalesFilterTypes.AndFilter,
      filters: excludedFilters,
    },
  };

  return {
    type: DirectSalesFilterTypes.AndFilter,
    filters: [notFilter, ...includedFilters],
  };
};

export const transformPoiFilters = (poi: POI[]): DirectSalesFilter => {
  const includedFilters: DirectSalesPOIFilter[] = [];
  const excludedFilters: DirectSalesPOIFilter[] = [];
  const customPoiIncludedFilters: DirectSalesCustomPOIFilter[] = [];
  const customPoiExcludedFilters: DirectSalesCustomPOIFilter[] = [];

  const customPoiFilters = poi.filter((poiItem) => poiItem.dataProvider === CUSTOM_POI_DATA_PROVIDER);

  if (customPoiFilters.length > 0) {
    const { includedCustomPoiFilters, excludedCustomPoiFilters } = transformCustomPoiToFilter(customPoiFilters);

    customPoiIncludedFilters.push(...includedCustomPoiFilters);
    customPoiExcludedFilters.push(...excludedCustomPoiFilters);
  }

  const poiFilters =
    customPoiFilters.length > 0 ? poi.filter((poiItem) => poiItem.dataProvider !== CUSTOM_POI_DATA_PROVIDER) : poi;

  poiFilters.forEach((poiItem) => {
    const { selectionType, ...filterData } = transformPoiToFilter(poiItem);

    if (poiItem.include) {
      includedFilters.push(filterData);
    } else {
      excludedFilters.push(filterData);
    }
  });

  const notFilter = {
    type: DirectSalesFilterTypes.NotFilter,
    filter: {
      type: DirectSalesFilterTypes.AndFilter,
      filters: [...excludedFilters, ...customPoiExcludedFilters],
    },
  };

  return {
    type: DirectSalesFilterTypes.AndFilter,
    filters: [notFilter, ...includedFilters, ...customPoiIncludedFilters],
  };
};

export const transformChannelsFilters = (channels: Environment[]): DirectSalesFilterItem[] => {
  return channels.map((channel) => {
    return {
      type: 'ChannelFilter',
      id: channel.code,
      name: channel.name,
    };
  });
};

export const transformPoiFilter = (poiFilter: DirectSalesPOIFilter, include: boolean): POI => {
  const categoryType = splitAndTrimCategories(poiFilter.id);
  const parentCategoryTypeIndex = categoryType.length - (poiFilter.name ? 1 : 2);

  return {
    code: poiFilter.id,
    name: poiFilter.name || categoryType[categoryType.length - 1],
    include,
    radius: poiFilter.radius,
    dataProvider: poiFilter.dataProvider,
    hasChildren: !poiFilter.name,
    rootCategoryType: categoryType[0],
    parentCategory: categoryType.length > 1 ? categoryType[parentCategoryTypeIndex] : '',
    categoryPath: poiFilter.name ? `${poiFilter.id}/${poiFilter.name}` : poiFilter.id,
    tooltip: `${categoryType.join(' > ')}${poiFilter.name ? ` > ${poiFilter.name}` : ''}`,
  };
};

export const transformCustomPoiFilter = (customPoiFilter: DirectSalesCustomPOIFilter, include: boolean): POI[] => {
  const fileName = customPoiFilter.name;
  const categoryPath = `${CUSTOM_POI_DATA_PROVIDER}/${fileName}`;

  return customPoiFilter.values.map((value, index) => {
    const leafPath = `${categoryPath}/${value.name}-${index}`;

    return {
      code: fileName,
      name: value.name,
      include,
      radius: value.radius,
      dataProvider: CUSTOM_POI_DATA_PROVIDER,
      hasChildren: false,
      rootCategoryType: fileName,
      parentCategory: '',
      categoryPath: leafPath,
      tooltip: `${fileName} > ${value.name}`,
      poiPoints: [
        {
          id: `custom-${leafPath}`,
          name: value.name,
          geometry: { coordinates: [value.longitude, value.latitude] },
          properties: {
            categoryName: fileName,
            categoryType: fileName,
            address: '',
            postalCode: '',
            dataProvider: CUSTOM_POI_DATA_PROVIDER,
            mediaOwner: '',
            type: 'custom',
            visibility: { include, radius: value.radius },
          },
        },
      ],
    };
  });
};

export const reverseTransformDealLineFilters = (
  directSalesResponseFilters: DirectSalesFilter,
  filterNames: Record<string, string>,
): CodeNameModel[] => {
  const filters: CodeNameModel[] = [];
  const reverseFilterTypeNames = reversedFilterNames(filterNames);

  const processFilter = (filter: DirectSalesLogicFilterTypes, include: boolean): void => {
    if (filter.type === 'NetworkFilter') return;

    if (filter.type === 'PoiFilter' && reverseFilterTypeNames[filter.type] && 'dataProvider' in filter) {
      const transformedPoiFilter = transformPoiFilter(filter, include);

      filters.push(transformedPoiFilter);

      return;
    }

    if (filter.type === 'CustomPoiFilter' && reverseFilterTypeNames[filter.type] && 'values' in filter) {
      filters.push(...transformCustomPoiFilter(filter, include));

      return;
    }

    if ('id' in filter && 'name' in filter && reverseFilterTypeNames[filter.type]) {
      const tagFilterGroupName = (filter as DirectSalesFilterItem).groupName;
      filters.push({
        category: tagFilterGroupName ?? reverseFilterTypeNames[filter.type],
        code: filter.id,
        name: filter.name || '',
        include,
      });
    }
  };

  const isNestedFilter = (filter: DirectSalesLogicFilterTypes): filter is DirectSalesNestedFilter => {
    return 'filter' in filter && 'filters' in filter.filter;
  };

  directSalesResponseFilters.filters.forEach((filter) => {
    if (isNestedFilter(filter)) {
      filter.filter.filters.forEach((nested) => processFilter(nested, false));
    } else if ('filters' in filter && Array.isArray(filter.filters)) {
      filter.filters.forEach((nested) => processFilter(nested, true));
    } else {
      processFilter(filter, true);
    }
  });

  return filters;
};

export const transformUploadedFilesFromResponse = (
  listFiles: FileList[],
  includedUnitFilters: DirectSalesUnitIncludedExcludedFilter[],
  excludedUnitFilters: DirectSalesUnitIncludedExcludedFilter[],
): void => {
  return listFiles.forEach(({ name: filename, include: fileWideInclude, fileContents }) => {
    if (!fileContents?.length) return;

    if (fileWideInclude !== undefined) {
      if (fileWideInclude === true) {
        fileContents.forEach(({ code }) => includedUnitFilters.push({ code, filename }));
        return;
      }

      if (fileWideInclude === false) {
        fileContents.forEach(({ code }) => excludedUnitFilters.push({ code, filename }));
        return;
      }
    }

    fileContents.forEach(({ code, include: individualFrameInclude }) => {
      if (!code) return;

      if (individualFrameInclude) {
        includedUnitFilters.push({ code, filename });
      } else {
        excludedUnitFilters.push({ code, filename });
      }
    });
  });
};

export const transformIndividuallySelectedFramesFromResponse = (
  frameCodes: RouteFrameCode[],
  includedUnitFilters: DirectSalesUnitIncludedExcludedFilter[],
  excludedUnitFilters: DirectSalesUnitIncludedExcludedFilter[],
): void => {
  return frameCodes.forEach(({ code, include }) => {
    if (!code) return;

    if (include) {
      includedUnitFilters.push({ code, filename: null });
    } else {
      excludedUnitFilters.push({ code, filename: null });
    }
  });
};

export const mergeExcludedFrames = (
  excludedUnitFilters: DirectSalesUnitIncludedExcludedFilter[],
): DirectSalesNestedFilter => {
  const uniqueExcludedFilesNames = [...new Set(excludedUnitFilters.map(({ filename }) => filename))];

  const excludedFrames = uniqueExcludedFilesNames.map((filename) => {
    return {
      type: DirectSalesFilterTypes.NotFilter,
      filter: {
        type: DirectSalesFilterTypes.AndFilter,
        filters: [
          {
            type: DirectSalesFilterGroupNames.UnitListFilter,
            filename,
            unitsIds: excludedUnitFilters
              .filter((individualFrames) => individualFrames.filename === filename)
              .map(({ code }) => code),
          },
        ],
      },
    };
  });

  return {
    type: DirectSalesFilterTypes.NotFilter,
    filter: {
      type: DirectSalesFilterTypes.AndFilter,
      filters: excludedFrames.flatMap((item) => item.filter.filters),
    },
  };
};

export const mergeIncludedFrames = (
  includedUnitFilters: DirectSalesUnitIncludedExcludedFilter[],
  type: DirectSalesFilterGroupNames,
): DirectSalesUnitListFilter[] => {
  const uniqueIncludedFilesNames = [...new Set(includedUnitFilters.map(({ filename }) => filename))];

  return uniqueIncludedFilesNames.map((filename) => {
    return {
      type,
      filename,
      unitsIds: includedUnitFilters
        .filter((individualFrames) => individualFrames.filename === filename)
        .map(({ code }) => code),
    };
  });
};

export const transformUnitListFilters = ({
  listFiles,
  frameCodes,
  type,
}: {
  listFiles?: FileList[];
  frameCodes?: RouteFrameCode[];
  type: DirectSalesFilterGroupNames;
}): DirectSalesFilter | null => {
  if (!frameCodes?.length && !listFiles?.length) return null;

  const includedUnitFilters: DirectSalesUnitIncludedExcludedFilter[] = [];
  const excludedUnitFilters: DirectSalesUnitIncludedExcludedFilter[] = [];

  if (type === DirectSalesFilterGroupNames.UnitListFilter && listFiles?.length) {
    transformUploadedFilesFromResponse(listFiles, includedUnitFilters, excludedUnitFilters);
  }

  if (frameCodes?.length) {
    transformIndividuallySelectedFramesFromResponse(frameCodes, includedUnitFilters, excludedUnitFilters);
  }

  const includedFrames = mergeIncludedFrames(includedUnitFilters, type);
  const excludedFrames = mergeExcludedFrames(excludedUnitFilters);

  return {
    type: DirectSalesFilterTypes.AndFilter,
    filters: excludedUnitFilters.length ? [...includedFrames, excludedFrames] : [...includedFrames],
  };
};

const transformNetworkTargetsAsFilters = (networks: NetworkCriteriaType[]): { type: string; id: string }[] => {
  return networks.map(({ code }) => ({
    id: code,
    type: mappedFormatFilterName.Network,
  }));
};

export const transformSegmentsAsFilter = (
  selectedSegment?: SelectedSegment,
): {
  type: string;
  dataProvider: string;
  id: string;
  name: string;
  minThreshold: number;
  maxThreshold: number;
}[] => {
  if (!selectedSegment || Object.keys(selectedSegment).length === 0) return [];

  const segment = Object.values(selectedSegment)[0];

  return [
    {
      type: 'SecondaryAudienceFilter',
      dataProvider: segment.dataProvider ?? '',
      id: segment.segmentId ?? '',
      name: segment.segmentName ?? '',
      minThreshold: segment.minThreshold ?? 0,
      maxThreshold: segment.maxThreshold ?? 1,
    },
  ];
};

export const mergeFilters = (
  {
    dealLineFormats = [],
    locations = [],
    poi = [],
    tags = [],
    channels,
    mediaType,
    listFiles,
    routeFrameCodes,
    mapRouteFrameCodes = [],
    networks = [],
    segments,
  }: {
    dealLineFormats: CodeNameModel[];
    locations: CodeNameModel[];
    poi: POI[];
    tags: CodeNameModel[];
    channels: Environment[];
    mediaType?: DirectSalesMediaType;
    listFiles?: FileList[];
    routeFrameCodes?: RouteFrameCode[];
    mapRouteFrameCodes?: RouteFrameCode[];
    networks?: NetworkCriteriaType[];
    segments?: SelectedSegment;
  },
  // eslint-disable-next-line sonarjs/cognitive-complexity
): DirectSalesFilter => {
  const dealLineFilters = transformDealLineFilters(dealLineFormats, mappedFormatFilterName);
  const locationFilters = transformDealLineFilters(locations, mapLocationFilter);
  const poiFilters = transformPoiFilters(poi);
  const tagsFilters = transformDealLineFilters(tags, mapTagFilter);
  const transformedChannelsFilter = transformChannelsFilters(channels);
  const unitListFilter = transformUnitListFilters({
    listFiles,
    frameCodes: routeFrameCodes,
    type: DirectSalesFilterGroupNames.UnitListFilter,
  });
  const mapSelectionListFilter = transformUnitListFilters({
    frameCodes: mapRouteFrameCodes,
    type: DirectSalesFilterGroupNames.MapSelectionListFilter,
  });

  const networkFilters = transformNetworkTargetsAsFilters(networks);
  const mediaTypeFilter = mediaType
    ? {
        type: 'MediaTypeFilter',
        mediaType,
      }
    : null;
  const segmentsFilter = transformSegmentsAsFilter(segments);

  const dealLineExcludedFilters = dealLineFilters.filters.find(
    (filter) => filter.type === DirectSalesFilterTypes.NotFilter,
  ) as DirectSalesNestedFilter;
  const locationExcludedFilter = locationFilters.filters.find(
    (filter) => filter.type === DirectSalesFilterTypes.NotFilter,
  ) as DirectSalesNestedFilter;
  const poiExcludedFilter = poiFilters.filters.find(
    (filter) => filter.type === DirectSalesFilterTypes.NotFilter,
  ) as DirectSalesNestedFilter;
  const tagsExcludedFilter = tagsFilters.filters.find(
    (filter) => filter.type === DirectSalesFilterTypes.NotFilter,
  ) as DirectSalesNestedFilter;
  const unitListExcludedFilter = unitListFilter
    ? (unitListFilter.filters.find(
        (filter) => filter.type === DirectSalesFilterTypes.NotFilter,
      ) as DirectSalesNestedFilter)
    : { filter: { filters: [] } };

  const dealLineIncludedFilters = dealLineFilters.filters.filter(
    (filter) => filter.type !== DirectSalesFilterTypes.NotFilter,
  );
  const locationIncludedFilters = locationFilters.filters.filter(
    (filter) => filter.type !== DirectSalesFilterTypes.NotFilter,
  );
  const poiIncludedFilters = poiFilters.filters.filter((filter) => filter.type !== DirectSalesFilterTypes.NotFilter);
  const tagsIncludedFilters = tagsFilters.filters.filter((filter) => filter.type !== DirectSalesFilterTypes.NotFilter);
  const unitListIncludedFilter = unitListFilter
    ? unitListFilter.filters.filter((filter) => filter.type !== DirectSalesFilterTypes.NotFilter)
    : [];
  const mapSelectionListIncludedFilter = mapSelectionListFilter
    ? mapSelectionListFilter.filters.filter((filter) => filter.type !== DirectSalesFilterTypes.NotFilter)
    : [];

  const combinedExcludedFilters = [
    ...(dealLineExcludedFilters?.filter?.filters || []),
    ...(locationExcludedFilter?.filter?.filters || []),
    ...(poiExcludedFilter?.filter?.filters || []),
    ...(tagsExcludedFilter?.filter?.filters || []),
    ...(unitListExcludedFilter?.filter?.filters || []),
  ];

  const mergedExcludedFilter =
    combinedExcludedFilters.length > 0
      ? {
          type: DirectSalesFilterTypes.NotFilter,
          filter: {
            type: DirectSalesFilterTypes.OrFilter,
            filters: combinedExcludedFilters,
          },
        }
      : null;

  const channelsFilter =
    transformedChannelsFilter.length > 0
      ? {
          type: DirectSalesFilterTypes.OrFilter,
          filters: transformedChannelsFilter,
        }
      : null;

  const combinedIncludedFilters = [
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(dealLineIncludedFilters || [])],
    },
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(locationIncludedFilters || [])],
    },
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(poiIncludedFilters || [])],
    },
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(tagsIncludedFilters || [])],
    },
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(unitListIncludedFilter || [])],
    },
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(networkFilters || [])],
    },
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(mapSelectionListIncludedFilter || [])],
    },
    {
      type: DirectSalesFilterTypes.OrFilter,
      filters: [...(segmentsFilter || [])],
    },
  ].filter((filter) => filter.filters.length > 0);

  const mergeAllFilters = [
    mediaTypeFilter,
    channelsFilter,
    ...combinedIncludedFilters,
    ...(mergedExcludedFilter ? [mergedExcludedFilter] : []),
  ].filter((filter): filter is DirectSalesFilter => filter !== null);

  return {
    type: DirectSalesFilterTypes.AndFilter,
    filters: mergeAllFilters,
  };
};
